import { Form, Input, Select } from 'antd';
import { useState } from 'react';

import { bookingsKey, changeBookingStage } from '@/api/bookings';
import type { BookingStage } from '@/api/generated/enums';
import { availabilityKey, itemUnitsKey, type BlockHolder } from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { FormDrawer } from '@/components/FormDrawer';
import { Notice } from '@/components/PageHeading';
import { formatDate, formatDateTime } from '@core/format/dates';
import { holdCountdown } from '@/format/holds';
import { blockedReason } from '@core/session/capability';
import type { Session } from '@core/session/types';

/**
 * The other party's tentative claim, and what the counter may do about it.
 *
 * <p>
 * This is the screen the whole hold-visibility change exists for. A customer is at the counter
 * wanting a garment another customer is holding; the shop needs the name, the number, and how long
 * the claim has left, and then one of two answers — they confirmed, or they let it go.
 * </p>
 *
 * <p>
 * <strong>Shared by the availability search and the booking counter</strong>, and taking primitives
 * rather than either screen's row type, because those two disagree: one holds an
 * `AvailabilityBlock`, the other the counter's own flattened `HeldGarment`. A component taking both
 * would either grow a union or force one screen to fabricate the other's shape.
 * </p>
 *
 * <p>
 * <strong>Both actions go through the booking, never through the calendar row.</strong> Lifting the
 * block directly would free the dates while leaving the booking believing it still holds the
 * garment: its garments stay Allocated, pointing at a block that no longer applies, and the shop has
 * a confirmed-looking order for stock it has given away. The stage endpoint releases the block, the
 * turnaround block behind it, and the garments' claim together.
 * </p>
 *
 * <p>
 * Releasing is a <em>cancellation</em> and not an expiry: someone decided, and Section 8's no-show
 * analysis has to be able to tell a decision from a clock running out. That is also why the reason
 * is compulsory — "the customer went elsewhere" and "we gave it to a paying customer in front of us"
 * are different facts, and the second one is the shop's own decision to answer for later.
 * </p>
 */
export function HoldDrawer({
  serialCode,
  variantDisplayName,
  holder,
  fromDate,
  toDate,
  session,
  onClose,
}: {
  serialCode: string;
  variantDisplayName: string;
  holder: BlockHolder;
  /** The possession period the claim covers, for "holds 10–14 Dec". */
  fromDate: string;
  toDate: string;
  session: Session;
  onClose: () => void;
}) {
  const [form] = Form.useForm<{ reason?: string }>();
  const [intent, setIntent] = useState<'confirm' | 'release'>('release');

  // Someone who may not edit a booking still gets the name and the number — ringing the first party
  // is not a write, and it is the half of this that a counter clerk needs most. What they do not get
  // is the pair of buttons that would change somebody else's order.
  const blocked = blockedReason(session, 'RentalOrder:Edit');

  const act = useApiMutation<unknown, { stage: BookingStage; reason?: string | null }>({
    mutationFn: ({ stage, reason }) =>
      changeBookingStage(holder.bookingId, { stage, reason: reason ?? null }),
    success:
      intent === 'confirm'
        ? 'Confirmed — the garment stays with them.'
        : `Released. ${serialCode} is back on the rail.`,

    // `availabilityKey` is a prefix of the booking counter's own query key, so one invalidation
    // refreshes both screens — the rail the clerk is looking at must not still show the hold.
    invalidates: [availabilityKey, itemUnitsKey, bookingsKey],
    onSuccess: onClose,
  });

  const countdown = holdCountdown(holder.holdExpiresOn);

  return (
    <FormDrawer<{ reason?: string }>
      title={`Held: ${serialCode}`}
      subtitle={variantDisplayName}
      open
      onClose={onClose}
      form={form}
      onSubmit={(values) =>
        blocked
          ? onClose()
          : void submitVia(act, {
              stage: intent === 'confirm' ? 'Confirmed' : 'Cancelled',
              reason: values.reason ?? null,
            })
      }
      submitText={
        blocked ? 'Close' : intent === 'confirm' ? 'Confirm their booking' : 'Release the garment'
      }
      submitting={act.isPending}
      width={520}
    >
      <div className="border-border-color mb-4 flex flex-col gap-2 rounded border p-3">
        <span className="text-[13px] font-semibold text-gray-900">
          {holder.customerName || 'Unnamed customer'}
        </span>

        {holder.customerMobile && (
          <a
            href={`tel:${holder.customerMobile}`}
            className="font-mono-nums text-primary text-[15px] hover:underline"
          >
            {holder.customerMobile}
          </a>
        )}

        <span className="text-default text-[12px]">
          {holder.documentNumber ?? 'No number yet — a hold takes one only when it is confirmed'}
          {holder.eventDate ? ` · event ${formatDate(holder.eventDate)}` : ''}
        </span>

        <span className="font-mono-nums text-default text-[12px]">
          Holds {formatDate(fromDate)} – {formatDate(toDate)}
        </span>

        {holder.holdExpiresOn && (
          <span className="text-warning text-[12px]">
            Lapses {formatDateTime(holder.holdExpiresOn)}
            {countdown ? ` · ${countdown}` : ''}
          </span>
        )}
      </div>

      {blocked ? (
        <Notice tone="warning" title="You cannot change their booking">
          {blocked} Ringing them is still worth doing — pass the answer to whoever can.
        </Notice>
      ) : (
        <>
          <Notice tone="info" title="Ring them before you decide">
            A hold is a promise the shop made. Releasing it hands the garment to whoever is in front
            of you and leaves the first customer with nothing — worth one phone call first.
          </Notice>

          <Form.Item label="What happened" className="mt-4">
            <Select
              value={intent}
              onChange={setIntent}
              options={[
                { label: 'They let it go — release the garment', value: 'release' },
                { label: 'They confirmed — keep it for them', value: 'confirm' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason"
            // Compulsory only on the way out. Confirming needs no explanation — the customer said
            // yes — while releasing is the shop deciding something on their behalf.
            {...(intent === 'release'
              ? {
                  rules: [{ required: true, message: 'Say why the hold was released.' }],
                  extra:
                    'Kept on the booking and on the calendar row — "why did we lose this" has to have an answer.',
                }
              : {})}
          >
            <Input.TextArea rows={2} placeholder="Rang twice, no answer — released for a walk-in" />
          </Form.Item>
        </>
      )}
    </FormDrawer>
  );
}
